import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';

const NAV_LINKS = [
  { label: 'Start', to: '/' },
  { label: 'Love Shift Masterclass', to: '/love-shift-masterclass' },
  { label: 'Love Code', to: '/beziehungsheilung-fuer-frauen' },
  { label: 'Business Code', to: '/female-leadership-fuer-frauen' },
  { label: 'Shop', to: '/shop' },
  { label: 'Onlinekurse', to: '/shop' },
  { label: 'Anmelden', to: '/contact' },
];

const WORDS = ['Der', 'Love', 'Code'];

const LoveCodeHero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '22%']);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-[100svh] flex flex-col overflow-hidden">
      {/* Navigation */}
      <motion.nav
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
        className="relative z-50 flex items-center justify-between px-6 lg:px-10 py-5 border-b border-white/5 bg-dark/70 backdrop-blur-xl"
      >
        <Link to="/" className="text-gold font-serif text-xl tracking-widest"
          style={{ textShadow: '0 0 15px rgba(212,175,55,0.6)' }}>
          CF.
        </Link>
        <div className="hidden lg:flex items-center gap-6">
          {NAV_LINKS.map(l => (
            <Link key={l.label} to={l.to}
              className="text-[11px] uppercase tracking-widest text-cream/55 hover:text-gold transition-colors duration-500 whitespace-nowrap">
              {l.label}
            </Link>
          ))}
        </div>
      </motion.nav>

      {/* BG Layers */}
      <motion.div style={{ y }} className="absolute inset-0 z-0 scale-110">
        <div className="absolute inset-0 bg-dark" />
        {/* Animated silk-like gradients */}
        <motion.div
          animate={{ opacity: [0.4, 0.7, 0.4], scale: [1, 1.08, 1] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute inset-0"
          style={{ background: 'radial-gradient(ellipse at 60% 40%, rgba(212,175,55,0.08) 0%, transparent 55%)' }}
        />
        <motion.div
          animate={{ opacity: [0.3, 0.6, 0.3], x: [0, 20, 0] }}
          transition={{ duration: 11, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute inset-0"
          style={{ background: 'radial-gradient(ellipse at 30% 70%, rgba(114,47,55,0.14) 0%, transparent 50%)' }}
        />
        {/* Silk threads */}
        {[...Array(6)].map((_, i) => (
          <motion.div key={i}
            className="absolute top-0 bottom-0 w-[1px]"
            style={{
              left: `${15 + i * 14}%`,
              background: i % 2 === 0
                ? 'linear-gradient(to bottom, transparent, rgba(212,175,55,0.08), transparent)'
                : 'linear-gradient(to bottom, transparent, rgba(114,47,55,0.06), transparent)',
              filter: 'blur(1px)',
            }}
            animate={{ opacity: [0, 0.8, 0], scaleY: [0.5, 1, 0.5] }}
            transition={{ duration: 5 + i * 1.2, repeat: Infinity, delay: i * 0.8 }}
          />
        ))}
        {/* Film grain */}
        <div className="absolute inset-0 opacity-[0.025]"
          style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.85\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")', backgroundSize: '200px' }} />
      </motion.div>

      {/* Hero Content */}
      <motion.div style={{ opacity }}
        className="relative z-10 flex-1 flex flex-col items-center justify-center text-center px-6 py-24">
        <motion.p
          initial={{ opacity: 0, letterSpacing: '0.05em' }}
          animate={{ opacity: 1, letterSpacing: '0.45em' }}
          transition={{ duration: 2, delay: 0.3 }}
          className="text-gold text-xs uppercase font-semibold mb-10"
          style={{ textShadow: '0 0 20px rgba(212,175,55,0.8)' }}>
          Seelenpartner · Affäre · Intensive Verbindung
        </motion.p>

        <h1 className="text-6xl md:text-8xl lg:text-[110px] font-serif leading-none mb-4">
          {WORDS.map((word, wi) => (
            <motion.span key={wi}
              initial={{ opacity: 0, y: 60, filter: 'blur(20px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              transition={{ duration: 1, delay: 0.5 + wi * 0.25, ease: [0.16, 1, 0.3, 1] }}
              className={`block ${wi === 1 ? 'text-transparent bg-clip-text bg-gold-gradient' : 'text-cream'}`}
              style={wi === 1 ? { filter: 'drop-shadow(0 0 25px rgba(212,175,55,0.35))' } : { textShadow: '0 0 40px rgba(249,249,249,0.08)' }}>
              {word}
            </motion.span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          transition={{ duration: 1.5, delay: 1.5 }}
          className="text-xl md:text-2xl font-serif italic text-cream/75 mb-6 max-w-3xl leading-relaxed">
          Die Wahrheit über Liebe, Affären &{' '}
          <span className="text-wine-light" style={{ textShadow: '0 0 15px rgba(155,66,78,0.4)' }}>
            intensive Verbindungen.
          </span>
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 2 }}
          className="max-w-2xl mx-auto space-y-5 text-cream/60 font-light text-lg leading-relaxed mb-14">
          <p>
            Eine Affäre oder eine tiefe Seelenverbindung passiert niemals zufällig.
            Sie ist ein Spiegel, der dir zeigt, was in dir gesehen werden will.
            Es geht nicht nur um den anderen Menschen –{' '}
            <span className="text-cream font-serif italic">es geht um dich.</span>
          </p>
          <p>
            Diese Verbindung bringt Dinge an die Oberfläche, die du sonst oft vermeidest:
            deine tiefe Sehnsucht nach echter Liebe, deine Angst, verlassen zu werden,
            verborgene Abhängigkeiten und deinen Selbstwert. Deshalb fühlt sich alles so{' '}
            <span className="text-wine-light">überwältigend intensiv</span> an.
          </p>
        </motion.div>

        <motion.a href="#shift"
          initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 2.5 }}
          whileHover={{ scale: 1.06, boxShadow: '0 0 60px rgba(212,175,55,0.7), 0 0 120px rgba(212,175,55,0.3)' }}
          whileTap={{ scale: 0.97 }}
          className="inline-block px-14 py-5 bg-gold-gradient text-dark font-bold uppercase tracking-widest text-sm cursor-pointer"
          style={{ boxShadow: '0 0 30px rgba(212,175,55,0.4)', borderRadius: '2px' }}>
          Dein Love Shift beginnt hier →
        </motion.a>

        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 3.2 }}
          className="text-xs text-cream/25 mt-6 tracking-widest uppercase">
          Kostenfreies Erstgespräch verfügbar
        </motion.p>
      </motion.div>

      {/* Scroll Indicator */}
      <motion.div animate={{ y: [0, 14, 0], opacity: [0.2, 1, 0.2] }}
        transition={{ repeat: Infinity, duration: 3.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-10">
        <div className="w-1 h-1 rounded-full bg-gold" style={{ boxShadow: '0 0 12px rgba(212,175,55,1)' }} />
        <div className="w-[1px] h-16 bg-gradient-to-b from-gold via-gold/40 to-transparent" />
      </motion.div>
    </section>
  );
};

export default LoveCodeHero;